import styled from "styled-components";
import {
    LeftSection,
    SectionHeader,
    SectionTitle,
    ToggleContainer,
    ToggleLabel,
    Card,
    InputRow,
    InputLabel,
    Input,
    InputWrapper,
    ImageHeader,
    Select,
    AddButton,
    Th,
    DragHandle,
    OrderInput,
    ImageSection,
    IconButton,
    ViewButton,
} from "../shared/styled";

// ─── Layout ──────────────────────────────────────────────

export const Container = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 30px;
    padding: 20px;
    height: 100%;

    @media (max-width: 1200px) {
        gap: 20px;
        padding: 15px;
    }

    @media (max-width: 576px) {
        padding: 10px;
        gap: 15px;
    }
`;

export const Toggle = styled.button.attrs<{
    isActive: boolean;
    mainColor: string;
}>(({ isActive }) => ({
    type: "button",
    role: "switch",
    "aria-checked": isActive,
}))<{ isActive: boolean; mainColor: string }>`
    width: 50px;
    height: 26px;
    border-radius: 13px;
    border: none;
    background-color: ${({ isActive, mainColor }) =>
        isActive ? mainColor : "#ccc"};
    position: relative;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &::after {
        content: "";
        position: absolute;
        top: 3px;
        left: ${({ isActive }) => (isActive ? "27px" : "3px")};
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background-color: white;
        transition: left 0.2s ease;
    }
`;

export const TableWrapper = styled.div`
    max-height: 400px;
    overflow-y: auto;
    margin-top: 10px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    overflow-x: auto;

    &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
    }

    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 4px;

        &:hover {
            background: #555;
        }
    }
`;

export const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    table-layout: auto;
    min-width: 500px;
`;

export const Td = styled.td`
    padding: 12px;
    font-size: 13px;
    color: #333;
    border-bottom: 1px solid #f0f0f0;
    vertical-align: middle;
    white-space: nowrap;
`;

export const ImageThumbnail = styled.div`
    width: 48px;
    height: 48px;
    border-radius: 6px;
    overflow: hidden;
    background-color: #f5f5f5;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

export const SmallToggle = styled.button.attrs<{
    isActive: boolean;
    mainColor: string;
}>(({ isActive }) => ({
    type: "button",
    role: "switch",
    "aria-checked": isActive,
}))<{ isActive: boolean; mainColor: string }>`
    width: 40px;
    height: 22px;
    border-radius: 11px;
    border: none;
    background-color: ${({ isActive, mainColor }) =>
        isActive ? mainColor : "#ccc"};
    position: relative;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &::after {
        content: "";
        position: absolute;
        top: 2px;
        left: ${({ isActive }) => (isActive ? "20px" : "2px")};
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background-color: white;
        transition: left 0.2s ease;
    }
`;

export const ActionsCell = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
`;

export const DeleteButton = styled(IconButton)`
    color: #dc3545;

    &:hover {
        background-color: #dc354520;
    }
`;

// ─── Language Tabs ───────────────────────────────────────

export const LanguageTabs = styled.div`
    display: flex;
    gap: 5px;
    margin-bottom: 15px;
    flex-wrap: wrap;

    @media (max-width: 576px) {
        gap: 8px;
        justify-content: center;
    }
`;

export const LanguageTab = styled.button<{
    isActive: boolean;
    mainColor: string;
}>`
    padding: 6px 16px;
    border: 1px solid
        ${({ isActive, mainColor }) => (isActive ? mainColor : "#e0e0e0")};
    border-radius: 20px;
    background-color: ${({ isActive, mainColor }) =>
        isActive ? mainColor : "#fff"};
    color: ${({ isActive }) => (isActive ? "#fff" : "#333")};
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: ${({ mainColor }) => mainColor};
    }
`;

// ─── Input ───────────────────────────────────────────────

export const ResetButton = styled.button<{ mainColor: string }>`
    background: none;
    border: none;
    cursor: pointer;
    padding: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ mainColor }) => mainColor};
    transition: background-color 0.2s ease;
    border-radius: 4px;

    &:hover {
        background-color: ${({ mainColor }) => `${mainColor}20`};
    }

    svg {
        width: 18px;
        height: 18px;
    }
`;

// Shared modal primitives (see ../shared/styled).
export {
    ModalOverlay,
    ModalContent,
    ModalTitle,
    ModalText,
    ModalButtons,
    ModalButton,
} from "../shared/styled";

// Shared layout primitives (see ../shared/styled).
export {
    LeftSection,
    SectionHeader,
    SectionTitle,
    ToggleContainer,
    ToggleLabel,
    Card,
    InputRow,
    InputLabel,
    Input,
    InputWrapper,
    ImageHeader,
    Select,
    AddButton,
    Th,
    DragHandle,
    OrderInput,
    ImageSection,
    IconButton,
    ViewButton,
};
